import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/helpers/cn';
import styles from './Input.module.scss';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Identificador usado apenas por controles compostos, nunca pelo input nativo. */
  labelId?: string;
  /** Adorno a esquerda: icone ou prefixo curto. Decorativo. */
  prefix?: ReactNode;
  /** Adorno a direita: normalmente uma acao (limpar, alternar visibilidade). */
  suffix?: ReactNode;
  invalid?: boolean;
  /** `boxed` (padrao): campo com caixa e fundo. `underline` mostra so a linha inferior. */
  variant?: 'boxed' | 'underline';
}

/**
 * Campo de texto.
 *
 * Altura 40px no desktop e 48px em mobile; fonte 16px em telas pequenas, porque
 * abaixo disso o iOS aplica zoom automatico ao focar o campo.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { prefix, suffix, invalid, variant = 'boxed', className, labelId: _labelId, ...rest },
  ref,
) {
  return (
    <div
      className={cn(
        styles.wrapper,
        variant === 'underline' && styles.underline,
        invalid && styles.invalid,
        rest.disabled && styles.disabled,
        className,
      )}
    >
      {prefix ? (
        <span className={styles.prefix} aria-hidden="true">
          {prefix}
        </span>
      ) : null}
      <input ref={ref} className={styles.input} {...rest} />
      {suffix ? <span className={styles.suffix}>{suffix}</span> : null}
    </div>
  );
});
